import { useMemo, useCallback } from "react";
import { TrashIcon } from "@heroicons/react/outline";
import { ICellRendererParams } from "ag-grid-community";

import { InviteMember } from "../../../InviteMember";

import { Badge, BadgeVariant } from "@/components/ui/Badge";
import { TableColumn } from "@/components/ui/TableV2";
import { Action, DropdownMenuVariant } from "@/components/ui/DropdownMenu";
import { ContextMenuItems, ContextMenuVariant } from "@/components/ui/ContextMenu";
import { BasicTooltip } from "@/components/ui/Tooltip";
import { WorkspaceInvite } from "@/types/workspace-invite";
import { formatTimestamp } from "@/utils/timestamp";
import { useDeleteWorkspaceInvite } from "@/queries/workspace";
import { useConfirm } from "@/context/confirm-context";
import { ConfirmationModalVariant } from "@/components/ConfirmationModal";

export interface InviteTableRow {
  id: string;
  inviteId: number;
  workspaceName: string;
  status: "Pending" | "Expired";
  invitedAt: string;
  expiresAt: string;
}

interface UseInviteTableParams {
  inviteeEmail?: string;
  invites: WorkspaceInvite[];
  isOrgOwner: boolean;
  isInviteDialogOpen: boolean;
  setIsInviteDialogOpen: (open: boolean) => void;
  organizationId: number;
  onInviteDelete?: () => void;
  onInviteCreate?: () => void;
}

export const useInviteTable = ({
  inviteeEmail,
  invites,
  isOrgOwner,
  isInviteDialogOpen,
  setIsInviteDialogOpen,
  organizationId,
  onInviteDelete,
  onInviteCreate,
}: UseInviteTableParams) => {
  const deleteWorkspaceInvite = useDeleteWorkspaceInvite({
    message: "Invite cancelled successfully",
  });
  const { confirm } = useConfirm();

  const inviteTableData: InviteTableRow[] = useMemo(() => {
    if (!inviteeEmail) {
      return [];
    }
    return invites.map(invite => ({
      id: String(invite.id),
      inviteId: invite.id,
      workspaceName: invite.workspace?.name ?? "-",
      status: (invite.is_expired || new Date(invite.expires_at) < new Date()
        ? "Expired"
        : "Pending") as "Pending" | "Expired",
      invitedAt: invite.created_at ? formatTimestamp(invite.created_at) : "-",
      expiresAt: invite.expires_at ?? "",
    }));
  }, [inviteeEmail, invites]);

  const handleCancelSingleInvite = useCallback(
    async (row?: InviteTableRow) => {
      if (!row) {
        return;
      }
      const isConfirmed = await confirm?.({
        message: `Are you sure you want to cancel this invite to ${row.workspaceName}?`,
        headerVariant: ConfirmationModalVariant.destructive,
        saveButtonVariant: "destructiveOutline",
        exitButtonVariant: "outline",
      });
      if (isConfirmed) {
        await deleteWorkspaceInvite.mutateAsync(row.inviteId);
        onInviteDelete?.();
      }
    },
    [confirm, deleteWorkspaceInvite, onInviteDelete],
  );

  const inviteTableColumns: TableColumn<InviteTableRow>[] = useMemo(
    () => [
      {
        id: 1,
        name: "workspaceName",
        field: "workspaceName",
        header: "Workspace",
        minWidth: 200,
        cellRenderer: (params: ICellRendererParams<InviteTableRow>) => (
          <span className="text-sm text-gray-900">{params.data?.workspaceName}</span>
        ),
      },
      {
        id: 2,
        name: "status",
        header: "Status",
        cellRenderer: (params: ICellRendererParams<InviteTableRow>) => {
          const invite = params.data;
          if (!invite) {
            return "";
          }

          const isExpired =
            invite.status === "Expired" ||
            (invite.expiresAt && new Date(invite.expiresAt) < new Date());
          const hoursRemaining = Math.floor(
            (new Date(invite.expiresAt).getTime() - new Date().getTime()) / (1000 * 60 * 60),
          );

          const days = Math.floor(hoursRemaining / 24);

          const text =
            days > 1
              ? `Expires in ${days} day${days !== 1 ? "s" : ""}`
              : `Expires in ${hoursRemaining} hour${hoursRemaining !== 1 ? "s" : ""}`;

          return (
            <BasicTooltip content={<div className="text-xs">{text}</div>}>
              <Badge
                variant={isExpired ? BadgeVariant.RED : BadgeVariant.ORANGE}
                className="capitalize"
              >
                {isExpired ? "Expired" : "Pending"}
              </Badge>
            </BasicTooltip>
          );
        },
      },
      {
        id: 3,
        name: "invitedAt",
        field: "invitedAt",
        header: "Invited",
        minWidth: 140,
        cellRenderer: (params: ICellRendererParams<InviteTableRow>) => (
          <span className="text-sm text-gray-900">{params.data?.invitedAt}</span>
        ),
      },
    ],
    [],
  );

  const inviteTableActions: Action<InviteTableRow>[] = useMemo(
    () => [
      {
        id: "cancel-invite",
        label: "Cancel invite",
        icon: TrashIcon,
        variant: DropdownMenuVariant.DESTRUCTIVE,
        onClick: handleCancelSingleInvite,
      },
    ],
    [handleCancelSingleInvite],
  );

  const inviteTableContextMenuItems: ContextMenuItems<InviteTableRow>[] = useMemo(
    () => [
      {
        id: "cancel-invite",
        label: "Cancel invite",
        icon: TrashIcon,
        variant: ContextMenuVariant.DESTRUCTIVE,
        onClick: (row: InviteTableRow) => handleCancelSingleInvite(row),
      },
    ],
    [handleCancelSingleInvite],
  );

  const inviteToWorkspaceActionForInvitee = useMemo(() => {
    if (!isOrgOwner || !inviteeEmail) {
      return undefined;
    }
    const excludeIds = invites.map(i => i.workspace_id).filter((id): id is number => !!id);
    return (
      <InviteMember
        open={isInviteDialogOpen}
        setOpen={setIsInviteDialogOpen}
        hasTrigger={false}
        organizationId={organizationId}
        email={inviteeEmail}
        buttonLabel="Invite to Workspace"
        excludeWorkspaceIds={excludeIds}
        onInviteCreate={onInviteCreate}
      />
    );
  }, [
    isOrgOwner,
    inviteeEmail,
    invites,
    isInviteDialogOpen,
    setIsInviteDialogOpen,
    organizationId,
    onInviteCreate,
  ]);

  return {
    inviteTableData,
    inviteTableColumns,
    inviteTableActions,
    inviteTableContextMenuItems,
    inviteToWorkspaceActionForInvitee,
  };
};
